import {ChangeDetectionStrategy, Component, Inject, Injectable} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {AvstemningsstatusModell, GruppeId, GruppeMedlemListeModell, HtmlTekst, MøteId, ÅpneStengeForAvstemningSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatFormFieldModule} from "@angular/material/form-field";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {MatTooltipModule} from "@angular/material/tooltip";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {AvstemningDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningDatatjeneste";

const debug = initDebugger('ÅpneStengeForAvstemningDialogKomponent');

export enum Operasjon {åpne, stenge}

export type DialogParametre = {
    møteId: MøteId;
    gruppeId: GruppeId;
    stemmeberettigede: GruppeMedlemListeModell[];
    avstemningsstatus: AvstemningsstatusModell;
    operasjon: Operasjon;
};

@Component({
    selector: 'aapne-stenge-for-avstemning-dialog',
    templateUrl: 'åpne-stenge-for-avstemning-dialog.html',
    styleUrl: 'åpne-stenge-for-avstemning-dialog.scss',
    standalone: true,
    imports: [
        MatToolbarModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatFormFieldModule,
        MatDialogContent,
        MatDialogActions,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        VisSpinnerDirektiv,
        MatTooltipModule,
        MatSlideToggleModule,
        RiktekstRedigeringKomponent,
        TipsKomponent,
        MatDialogTitle
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ÅpneStengeForAvstemningDialogKomponent {

    static visÅpne(dialog: MatDialog, møteId: MøteId, gruppeId: GruppeId, stemmeberettigede: GruppeMedlemListeModell[], avstemningsstatus: AvstemningsstatusModell): MatDialogRef<ÅpneStengeForAvstemningDialogKomponent, boolean> {
        return ÅpneStengeForAvstemningDialogKomponent.vis(dialog, {møteId, gruppeId, stemmeberettigede, avstemningsstatus, operasjon: Operasjon.åpne});
    }

    static visStenge(dialog: MatDialog, møteId: MøteId, gruppeId: GruppeId, stemmeberettigede: GruppeMedlemListeModell[], avstemningsstatus: AvstemningsstatusModell): MatDialogRef<ÅpneStengeForAvstemningDialogKomponent, boolean> {
        return ÅpneStengeForAvstemningDialogKomponent.vis(dialog, {møteId, gruppeId, stemmeberettigede, avstemningsstatus, operasjon: Operasjon.stenge});
    }

    private static vis(dialog: MatDialog, parametre: DialogParametre): MatDialogRef<ÅpneStengeForAvstemningDialogKomponent, boolean> {
        return dialog.open<ÅpneStengeForAvstemningDialogKomponent, DialogParametre, boolean>(ÅpneStengeForAvstemningDialogKomponent, {
            data: parametre,
            disableClose: true,
            minWidth: '500px',
            minHeight: '460px' //sette min høyde som kreves når tekstfelt for melding vises slik at ikke dialog 'hopper' nå 'informer'-valget ikke er avhuket
        });
    }

    readonly stemmeberettigede: GruppeMedlemListeModell[]
    readonly avstemningsstatus: AvstemningsstatusModell;
    readonly erAapneOperasjon: boolean;
    readonly gruppeId: GruppeId;

    readonly skjema = new FormGroup({
        sendMelding: new FormControl(true, {nonNullable: true}),
        beskjed: new FormControl<HtmlTekst>('', {nonNullable: true, validators: FeltValidatorer.htmlTekst}),
        valgteMottakere: new FormControl<GruppeMedlemListeModell[]>([], {nonNullable: true}),
    });

    readonly lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());
    private møteId: MøteId;

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<ÅpneStengeForAvstemningDialogKomponent, boolean>,
                private avhengigheter: ÅpneStengeForAvstemningDialogKomponentAvhengigheter) {
        this.stemmeberettigede = parametre.stemmeberettigede;
        this.avstemningsstatus = parametre.avstemningsstatus;
        this.gruppeId = parametre.gruppeId;
        this.møteId = parametre.møteId;
        this.erAapneOperasjon = parametre.operasjon === Operasjon.åpne;
    }

    async lagre() {
        const spesifikasjon: ÅpneStengeForAvstemningSpesifikasjon = {
            informerStemmeberettigede: this.skjema.value.sendMelding!,
            beskjed: this.skjema.controls.beskjed.value ?? ''
        };
        await this.lagrer.utfør(async () => this.erAapneOperasjon ?
          this.avhengigheter.åpneForAvstemning(this.møteId, spesifikasjon) :
          this.avhengigheter.stengForAvstemning(this.møteId, spesifikasjon))
        this.ferdig();
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }
}

@Injectable({providedIn: 'root'})
export class ÅpneStengeForAvstemningDialogKomponentAvhengigheter {
    constructor(private avstemningDatatjeneste: AvstemningDatatjeneste) {
    }

    async åpneForAvstemning(id: MøteId, spesifikasjon: ÅpneStengeForAvstemningSpesifikasjon): Promise<void> {
        await this.avstemningDatatjeneste.åpneForAlleAvstemninger(id, spesifikasjon);
    }

    async stengForAvstemning(id: MøteId, spesifikasjon: ÅpneStengeForAvstemningSpesifikasjon): Promise<void> {
        await this.avstemningDatatjeneste.stengForAlleAvstemninger(id, spesifikasjon);
    }
}
